import React, { useEffect, useState } from 'react';
import { API_ACCOUNT_TRANSLATE_OFFER, API_COPY_LOCALES } from '../../common/EsConstants';
import { esPut, esRead } from '../../common/EsFetch';
import { useEsStrings } from '../../common/EsStrings';

const EN_US = 'en-US';

/** The offer as esback stores it, a comma list, as the tags it names. */
export const offerTags = ( pOffer ) => ( pOffer ? String( pOffer ).split( ',' ).filter( Boolean ) : [] );

/** Every group but the source, tier order then native name: whether it is live yet is not the question. */
export const offerChoices = ( pLocales ) => ( pLocales ?? [] )
    .filter( ( pOne ) => pOne.key !== EN_US )
    .sort( ( pOne, pTwo ) => ( pOne.tier - pTwo.tier ) || String( pOne.nativeName ).localeCompare( String( pTwo.nativeName ) ) );

/* The account page's "I can translate": the locale groups this reader offers to translate into,
   one press each, saved at once. The translator recruitment view ranks an offer above
   everything else it knows about a candidate. Drawn while rolloutEsTranslations is on. */
export default function EsTranslateOffer( { offer } ) {
    const t = useEsStrings();
    const [ locales, setLocales ] = useState( [] );
    const [ chosen, setChosen ] = useState( () => offerTags( offer ) );
    const [ busy, setBusy ] = useState( false );
    const [ error, setError ] = useState( null );

    useEffect( () => { setChosen( offerTags( offer ) ); }, [ offer ] );

    // Read here rather than from the nav's list: a group made a moment ago is offerable now.
    useEffect( () => {
        esRead( API_COPY_LOCALES )
            .then( ( pList ) => setLocales( offerChoices( pList ) ) )
            .catch( () => setLocales( [] ) );
    }, [] );

    const toggle = async ( pKey ) => {
        const aNext = chosen.includes( pKey ) ? chosen.filter( ( pOne ) => pOne !== pKey ) : [ ...chosen, pKey ];
        setBusy( true );
        setError( null );
        try {
            const aSaved = await esPut( API_ACCOUNT_TRANSLATE_OFFER, { tags: aNext } );
            setChosen( aSaved?.translateOffer ?? aNext );
        }
        catch ( aError ) {
            setError( aError?.message ?? t( 'account-settings-translate-not-saved', 'That did not save.' ) );
        }
        finally {
            setBusy( false );
        }
    };

    if ( locales.length === 0 ) return null;

    return (
        <div data-testid="es-account-translate-offer">
            <p className="mb-1 text-xs opacity-70">
                { t( 'account-settings-translate-hint', 'Pick any you could translate the site into. We may ask you to help.' ) }
            </p>
            <div className="flex flex-wrap items-center gap-2 text-sm" role="group"
                 aria-label={ t( 'account-settings-translate', 'I can translate' ) }>
                { locales.map( ( pGroup ) => (
                    <button key={ pGroup.key } type="button" data-testid={ `es-account-translate-offer-${ pGroup.key }` }
                            lang={ pGroup.key } disabled={ busy }
                            aria-pressed={ chosen.includes( pGroup.key ) }
                            onClick={ () => toggle( pGroup.key ) }
                            className={ 'rounded-sm px-1 text-body ring-focus focus:ring-2 disabled:opacity-50 '
                                + ( chosen.includes( pGroup.key ) ? 'font-bold underline' : 'opacity-70 hover:opacity-100' ) }>
                        { pGroup.nativeName ?? pGroup.key }
                    </button>
                ) ) }
            </div>
            { error && <p data-testid="es-account-translate-offer-error" role="alert" className="mt-2 text-xs text-danger">{ error }</p> }
        </div>
    );
}
